import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import { Link as RouterLink } from 'react-router-dom';
import { Grid, Box, Link, Skeleton } from '@mui/material';
import { AppSiteStatusCard } from '../sections/@dashboard/app';
import { fetchBessStatusBySystemId } from '../redux/slices/bessSlice';
import {
  getModeText,
  getCardColor,
  getChargingStatus,
  getSocPercent,
} from '../utils/apiCommonMethods';

export default function SystemCard({ system, msSessionInfo, refreshCounter }) {
  // ============= Initialization =============
  const dispatch = useDispatch();
  const bessData = useSelector((state) => state.bess.data);
  // ref is used to track if the component is in view
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });
  const [hasFetched, setHasFetched] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [localRefreshCount, setLocalRefreshCount] = useState(0);

  // ============= Effect to fetch data =============
  // This effect will trigger a fetch when the component is in view and the data has not been fetched yet
  useEffect(() => {
    const shouldRefetch =
      inView && msSessionInfo && (!hasFetched || refreshCounter !== localRefreshCount);
    // If the component is in view and we haven't fetched data yet or the refresh counter has changed
    // then we fetch the data
    if (shouldRefetch) {
      // Marking that we have started fetching data, need to prevent multiple fetches
      setHasFetched(true);
      setCompleted(false);
      dispatch(fetchBessStatusBySystemId({ msSessionInfo, systemIds: [system.id] })).finally(() => {
        setCompleted(true);
        setLocalRefreshCount(refreshCounter);
      });
    }
  }, [inView, msSessionInfo, refreshCounter, localRefreshCount, hasFetched, dispatch, system.id]);

  // ============= Prepare data for rendering =============
  // Find the BESS entry for the current system

  const bessEntry = bessData.find((b) => b.systemId === system.id);
  const isOffline = completed && !bessEntry;
  const modeText = getModeText(bessEntry?.mode, bessEntry?.stsId);

  // Determine card color based on loading state and BESS entry
  let cardColor;
  if (!completed) {
    cardColor = 'disable';
  } else if (bessEntry) {
    cardColor = getCardColor(modeText, bessEntry.power);
  } else {
    cardColor = 'warning';
  }

  // Determine charging status and SoC text based on BESS entry
  let chargingStatus;
  if (!completed) {
    chargingStatus = null;
  } else if (isOffline) {
    chargingStatus = 'Offline';
  } else if (bessEntry?.power !== undefined) {
    chargingStatus = getChargingStatus(bessEntry.power, modeText);
  } else {
    chargingStatus = 'Unknown';
  }

  // Determine SoC text based on BESS entry
  let socText;
  if (!completed) {
    socText = null;
  } else if (isOffline) {
    socText = '- % SOC';
  } else if (bessEntry?.soc !== undefined) {
    socText = `${getSocPercent(bessEntry.soc)} SOC`;
  } else {
    socText = '- % SOC';
  }

  return (
    <Grid item xs={12} sm={6} md={4} ref={ref}>
      <Link component={RouterLink} to={`/dashboard/sitestatus/${system.id}`} underline="none">
        <AppSiteStatusCard
          title={(system.name || 'Unknown').slice(0, 20)}
          subtitle={
            <Box
              sx={{
                height: 25,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              {chargingStatus !== null ? (
                chargingStatus
              ) : (
                <Skeleton variant="text" width={90} height={20} />
              )}
            </Box>
          }
          subsubtitle={
            <Box
              sx={{
                height: 25,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              {socText !== null ? (
                socText
              ) : (
                <Skeleton variant="text" width={70} height={20} />
              )}
            </Box>
          }
          color={cardColor}
        />
      </Link>
    </Grid>
  );
}